/**
 * ============================================================================
 * Production 模块 - 工单分配类型定义
 * ============================================================================
 *
 * 📋 文件职责：
 * - 定义工单分配预览、确认相关的类型
 * - 定义产线产能查询相关的类型
 *
 * 🔄 更新时机：
 * - 后端 API 变更时
 * - 新增分配相关功能时
 *
 * @module types/production/dispatch
 * @generated Manual (AI-assisted)
 * @last-updated 2025-12-01
 * ============================================================================
 */

import type { ProductionOrder, WorkOrder } from './responses';

// ============================================================================
// 分配策略
// ============================================================================

/** 分配策略类型 */
export type DispatchStrategy = 'balanced' | 'fastest' | 'least_change' | 'fixed_quantity';

/** 产能计算模式 */
export type CapacityMode = 'configured' | 'historical' | 'auto';

/** 分配策略选项 */
export const DISPATCH_STRATEGY_OPTIONS: {
  value: DispatchStrategy;
  label: string;
  description: string;
}[] = [
  {
    value: 'balanced',
    label: '均衡分配',
    description: '均衡分配到各产线，避免单条线超负荷',
  },
  {
    value: 'fastest',
    label: '最快完成',
    description: '优先使用空闲资源，尽快完成订单',
  },
  {
    value: 'least_change',
    label: '最小变动',
    description: '优先使用历史分配方案，减少调整',
  },
  {
    value: 'fixed_quantity',
    label: '按固定量分配',
    description: '按指定数量拆分工单，总量÷固定量=工单数，余数为独立工单',
  },
];

/** 产能计算模式选项 */
export const CAPACITY_MODE_OPTIONS: {
  value: CapacityMode;
  label: string;
  description: string;
}[] = [
  {
    value: 'auto',
    label: '自动',
    description: '优先使用配置值，无配置时使用历史数据计算',
  },
  {
    value: 'configured',
    label: '配置值',
    description: '使用产线管理中配置的日产能',
  },
  {
    value: 'historical',
    label: '历史计算',
    description: '基于近30天实际产出计算平均日产能',
  },
];

// ============================================================================
// 建议工单
// ============================================================================

/** 建议工单（预览阶段的临时工单） */
export interface SuggestedWorkOrder {
  /** 临时ID（前端生成，用于标识） */
  tempId: string;
  /** 批次号 */
  batchNo: string;
  /** 物料ID */
  materialId: number;
  /** 物料编码 */
  materialCode: string;
  /** 物料名称 */
  materialName: string;
  /** 生产线ID */
  lineId: number;
  /** 生产线编码 */
  lineCode: string;
  /** 生产线名称 */
  lineName: string;
  /** 工艺路线ID */
  routeId: number;
  /** 工艺路线编码 */
  routeCode: string;
  /** 工单数量 */
  workOrderQuantity: number;
  /** 工单计划开始日期 */
  planStartDate: string;
  /** 工单计划结束日期 */
  planEndDate: string;
  /** 工单备注（分配时填写，随工单落库） */
  remark?: string | null;
}

// ============================================================================
// 分配冲突
// ============================================================================

/** 冲突类型 */
export type DispatchConflictType =
  | 'capacity_overload' // 产能超负荷
  | 'time_conflict' // 时间冲突
  | 'resource_unavailable' // 资源不可用
  | 'material_shortage'; // 物料不足

/** 冲突严重程度 */
export type DispatchConflictSeverity = 'warning' | 'error';

/** 分配冲突 */
export interface DispatchConflict {
  /** 冲突类型 */
  type: DispatchConflictType;
  /** 严重程度 */
  severity: DispatchConflictSeverity;
  /** 冲突描述 */
  message: string;
  /** 受影响的工单临时ID */
  affectedWorkOrderTempId?: string | null;
  /** 建议方案 */
  suggestions?: string[];
}

/** 冲突类型标签 */
export const DISPATCH_CONFLICT_TYPE_LABELS: Record<DispatchConflictType, string> = {
  capacity_overload: '产能超负荷',
  time_conflict: '时间冲突',
  resource_unavailable: '资源不可用',
  material_shortage: '物料不足',
};

// ============================================================================
// 产能利用
// ============================================================================

/** 产能利用情况 */
export interface CapacityUtilization {
  /** 生产线ID */
  lineId: number;
  /** 生产线编码 */
  lineCode: string;
  /** 生产线名称 */
  lineName: string;
  /** 日期 */
  date: string;
  /** 产能（件/天） */
  capacity: number;
  /** 分配前已占用 */
  allocatedBefore: number;
  /** 分配后占用 */
  allocatedAfter: number;
  /** 利用率（百分比，如 0.8 表示 80%） */
  utilizationRate: number;
  /** 是否超负荷 */
  overloaded?: boolean;
}

/** 每日负荷 */
export interface DailyLoad {
  /** 日期 */
  date: string;
  /** 产能 */
  capacity: number;
  /** 已分配 */
  allocated: number;
  /** 可用 */
  available: number;
  /** 利用率 */
  utilizationRate: number;
  /** 是否超负荷 */
  overloaded: boolean;
}

/** 按产线汇总的产能 */
export interface LineCapacitySummary {
  /** 生产线ID */
  lineId: number;
  /** 生产线编码 */
  lineCode: string;
  /** 生产线名称 */
  lineName: string;
  /** 日产能 */
  dailyCapacity: number;
  /** 每日负荷明细 */
  dailyLoads: DailyLoad[];
}

// ============================================================================
// API 请求类型
// ============================================================================

/** 分配预览请求 */
export interface DispatchPreviewRequest {
  /** 分配策略 */
  strategy?: DispatchStrategy;
  /** 产能计算模式 */
  capacityMode?: CapacityMode;
}

/** 确认分配请求 */
export interface DispatchConfirmRequest {
  /** 要创建的工单列表 */
  workOrders: Omit<SuggestedWorkOrder, 'tempId'>[];
}

/** 产线产能查询参数 */
export interface LineCapacityQueryParams {
  /** 生产线ID数组（空则查询全部） */
  lineIds?: number[];
  /** 开始日期 */
  startDate: string;
  /** 结束日期 */
  endDate: string;
}

// ============================================================================
// API 响应类型
// ============================================================================

/** 分配数量信息 */
export interface AllocationInfo {
  /** 计划总量 */
  orderQuantity: number;
  /** 已分配数量 */
  allocatedQuantity: number;
  /** 剩余可分配数量 */
  remainingQuantity: number;
}

/** 分配预览响应 */
export interface DispatchPreviewResult {
  /** 生产订单信息 */
  productionOrder: ProductionOrder;
  /** 建议的工单列表 */
  suggestedWorkOrders: SuggestedWorkOrder[];
  /** 冲突警告 */
  conflicts: DispatchConflict[];
  /** 产能利用情况 */
  capacityUtilization: CapacityUtilization[];
  /** 使用的产能计算模式 */
  capacityMode?: CapacityMode;
  /** 分配数量信息 */
  allocationInfo?: AllocationInfo;
}

/** 分配统计摘要 */
export interface DispatchSummary {
  /** 主工单数量 */
  mainWorkOrders: number;
  /** 子工单数量（半成品工单） */
  childWorkOrders: number;
  /** 总工单数量 */
  totalWorkOrders: number;
}

/** 确认分配响应 */
export interface DispatchConfirmResult {
  /** 更新后的生产订单 */
  productionOrder: ProductionOrder;
  /** 创建的工单列表 */
  workOrders: WorkOrder[];
  /** 分配统计摘要 */
  summary?: DispatchSummary;
  /** 警告信息（如半成品缺少工艺路线等） */
  warnings?: string[];
}
